<template>
  <header>
    <div id="header-sticky" :class="['aipath-header', { sticky: isSticky }]">
      <div class="container-fluid">
        <div class="nav-shell">
          <router-link to="/" class="brand-lockup" aria-label="AiPath home">
            <img :src="require('../../assets/img/logo/LogoPg-smaller.webp')" alt="AiPath company logo" />
            <span>AiPath</span>
          </router-link>

          <nav class="nav-links" aria-label="Primary navigation">
            <div class="nav-item nav-item--dropdown">
              <router-link to="/services">{{ $t("menu_services") }}</router-link>
              <div class="nav-dropdown nav-dropdown--wide">
                <router-link v-for="item in solutionLinks" :key="item.path" :to="item.path" class="dropdown-card">
                  <strong>{{ item.title }}</strong>
                  <span>{{ item.text }}</span>
                </router-link>
              </div>
            </div>
            <a href="/#case-studies" class="nav-item-link">{{ $t("menu_projects") }}</a>
            <router-link to="/machine-learning-solutions" class="nav-item-link">{{ $t("menu_research") }}</router-link>
            <router-link to="/website-development" class="nav-item-link">{{ $t("menu_sites") }}</router-link>
            <div class="nav-item nav-item--dropdown">
              <router-link to="/about">{{ $t("menu_about") }}</router-link>
              <div class="nav-dropdown nav-dropdown--compact">
                <router-link v-for="item in companyLinks" :key="item.path" :to="item.path">{{ item.label }}</router-link>
              </div>
            </div>
          </nav>

          <div class="nav-actions">
            <button class="language-pill" type="button" @click="toggleLanguage" :aria-label="`Switch language to ${nextLanguageLabel}`">
              {{ currentLanguageLabel }}
            </button>
            <router-link to="/project-idea" class="nav-cta">{{ $t("menu_getOffer") }}</router-link>
            <button class="mobile-toggle" type="button" @click="menuOption.menuShow = !menuOption.menuShow" aria-label="Open menu">
              <span></span><span></span><span></span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <transition name="mobile-panel">
      <div v-if="menuOption.menuShow" class="mobile-nav-wrap">
        <div class="mobile-nav-panel">
          <div class="mobile-nav-head">
            <router-link to="/" class="brand-lockup" @click="closeMenu">
              <img :src="require('../../assets/img/logo/LogoPg-smaller.webp')" alt="AiPath company logo" />
              <span>AiPath</span>
            </router-link>
            <button type="button" @click="closeMenu" aria-label="Close menu">×</button>
          </div>

          <div class="mobile-section">
            <p>{{ $t("menu_services") }}</p>
            <router-link v-for="item in solutionLinks" :key="item.path" :to="item.path" @click="closeMenu">
              {{ item.title }}
            </router-link>
          </div>

          <div class="mobile-section mobile-section--grid">
            <a href="/#case-studies" @click="closeMenu">{{ $t("menu_projects") }}</a>
            <router-link to="/machine-learning-solutions" @click="closeMenu">{{ $t("menu_research") }}</router-link>
            <router-link to="/website-development" @click="closeMenu">{{ $t("menu_sites") }}</router-link>
            <router-link to="/about" @click="closeMenu">{{ $t("menu_about") }}</router-link>
            <router-link to="/contact" @click="closeMenu">{{ $t("menu_contact") }}</router-link>
          </div>

          <router-link to="/project-idea" class="mobile-cta" @click="closeMenu">{{ $t("menu_startProject") }}</router-link>
        </div>
        <button class="mobile-backdrop" type="button" aria-label="Close menu" @click="closeMenu"></button>
      </div>
    </transition>
  </header>
</template>

<script>
export default {
  name: "HomeHeader",
  data() {
    return {
      isSticky: false,
      menuOption: { menuShow: false },
    };
  },
  computed: {
    currentMessages() {
      return this.$i18n?.getLocaleMessage(this.$i18n.locale) || {};
    },
    solutionLinks() {
      return this.currentMessages.nav?.solutions || [];
    },
    companyLinks() {
      return this.currentMessages.nav?.company || [];
    },
    currentLanguageLabel() {
      return this.$i18n?.locale === "en" ? "EN" : "GR";
    },
    nextLanguageLabel() {
      return this.$i18n?.locale === "en" ? "Greek" : "English";
    },
  },
  methods: {
    handleSticky() {
      this.isSticky = window.scrollY > 24;
    },
    closeMenu() {
      this.menuOption.menuShow = false;
    },
    toggleLanguage() {
      if (this.$i18n) {
        const nextLocale = this.$i18n.locale === "en" ? "gr" : "en";
        this.$i18n.locale = nextLocale;
        localStorage.setItem("aipath-locale", nextLocale);
        document.documentElement.setAttribute("lang", nextLocale === "en" ? "en" : "el");
        window.dispatchEvent(new CustomEvent("aipath-locale-change"));
      }
    },
  },
  mounted() {
    const savedLocale = localStorage.getItem("aipath-locale");
    if (this.$i18n && ["gr", "en"].includes(savedLocale)) {
      this.$i18n.locale = savedLocale;
      document.documentElement.setAttribute("lang", savedLocale === "en" ? "en" : "el");
    }
    this.handleSticky();
    window.addEventListener("scroll", this.handleSticky, { passive: true });
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.handleSticky);
  },
};
</script>

<style scoped>
.aipath-header {
  position: fixed;
  inset: 0 0 auto 0;
  z-index: 999;
  padding: 18px 18px 0;
  transition: padding 0.25s ease, background 0.25s ease;
}
.aipath-header.sticky {
  padding-top: 10px;
}
.nav-shell {
  min-height: 76px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 12px 14px 12px 18px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 16px;
  background: rgba(7, 26, 44, 0.96);
  box-shadow: 0 18px 48px rgba(7, 26, 44, 0.24);
}
.sticky .nav-shell {
  min-height: 76px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 12px 14px 12px 18px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 16px;
  background: rgba(7, 26, 44, 0.96);
  box-shadow: 0 18px 48px rgba(7, 26, 44, 0.24);
}
.brand-lockup {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #fff;
  font-weight: 800;
  font-size: 26px;
  letter-spacing: 0;
}
.brand-lockup img {
  width: 42px;
  height: 42px;
  object-fit: contain;
}
.nav-links {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex: 1;
}
.nav-item,
.nav-item-link,
.nav-item > a {
  position: relative;
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 0 13px;
  color: rgba(255, 255, 255, 0.86);
  font-size: 15px;
  font-weight: 700;
}
.nav-item-link:hover,
.nav-item > a:hover {
  color: #fff;
}
.nav-item--dropdown > a::after {
  content: "";
  width: 7px;
  height: 7px;
  margin-left: 8px;
  border-right: 1px solid currentColor;
  border-bottom: 1px solid currentColor;
  transform: rotate(45deg) translateY(-2px);
}
.nav-dropdown {
  position: absolute;
  top: calc(100% + 12px);
  left: 50%;
  z-index: 20;
  display: grid;
  gap: 10px;
  opacity: 0;
  visibility: hidden;
  transform: translate(-50%, 8px);
  padding: 14px;
  border: 1px solid rgba(12, 25, 43, 0.08);
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 26px 80px rgba(8, 18, 31, 0.22);
  transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s ease;
}
.nav-item--dropdown:hover .nav-dropdown,
.nav-item--dropdown:focus-within .nav-dropdown {
  opacity: 1;
  visibility: visible;
  transform: translate(-50%, 0);
}
.nav-dropdown--wide {
  width: min(760px, calc(100vw - 40px));
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.nav-dropdown--compact {
  width: 230px;
}
.dropdown-card,
.nav-dropdown--compact a {
  display: grid;
  gap: 4px;
  padding: 13px;
  border-radius: 12px;
  color: #071A2C;
  background: #fff;
}
.dropdown-card:hover,
.nav-dropdown--compact a:hover {
  background: #F4F7F8;
  color: #0C3145;
}
.dropdown-card strong {
  font-size: 15px;
  line-height: 1.2;
}
.dropdown-card span {
  color: #607087;
  font-size: 13px;
  line-height: 1.35;
}
.nav-actions {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}
.language-pill,
.nav-cta,
.mobile-cta {
  border: 0;
  border-radius: 14px;
  font-weight: 800;
  white-space: nowrap;
}
.language-pill {
  width: 44px;
  height: 44px;
  color: #fff;
  background: rgba(255, 255, 255, 0.10);
}
.nav-cta,
.mobile-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0 18px;
  color: #071A2C;
  background: #9DF4C3;
}
.nav-cta:hover,
.mobile-cta:hover {
  color: #071A2C;
  transform: translateY(-1px);
}
.mobile-toggle {
  display: none;
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.10);
}
.mobile-toggle span {
  display: block;
  width: 20px;
  height: 2px;
  margin: 4px auto;
  background: #fff;
}
.mobile-nav-wrap {
  position: fixed;
  inset: 0;
  z-index: 1000;
}
.mobile-backdrop {
  position: absolute;
  inset: 0;
  border: 0;
  background: rgba(5, 12, 23, 0.56);
}
.mobile-nav-panel {
  position: absolute;
  inset: 12px 12px auto 12px;
  z-index: 2;
  display: grid;
  gap: 18px;
  max-height: calc(100vh - 24px);
  overflow: auto;
  overscroll-behavior: contain;
  padding: 18px;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 28px 90px rgba(4, 11, 20, 0.28);
}
.mobile-nav-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.mobile-nav-head .brand-lockup {
  color: #071A2C;
}
.mobile-nav-head button {
  width: 42px;
  height: 42px;
  border: 0;
  border-radius: 50%;
  color: #071A2C;
  background: #eef4f7;
  font-size: 28px;
  line-height: 1;
}
.mobile-section {
  display: grid;
  gap: 8px;
}
.mobile-section p {
  margin: 0 0 4px;
  color: #6a7788;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.mobile-section a {
  padding: 13px 14px;
  border-radius: 12px;
  color: #071A2C;
  background: #F4F7F8;
  font-weight: 800;
}
.mobile-section--grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.mobile-cta {
  min-height: 52px;
}
.mobile-panel-enter-active,
.mobile-panel-leave-active {
  transition: opacity 0.2s ease;
}
.mobile-panel-enter-from,
.mobile-panel-leave-to {
  opacity: 0;
}
@media (max-width: 1180px) {
  .nav-links,
  .nav-cta {
    display: none;
  }
  .mobile-toggle {
    display: inline-block;
  }
}
@media (max-width: 640px) {
  .aipath-header {
    padding: 8px 8px 0;
  }
  .nav-shell {
    min-height: 60px;
    gap: 8px;
    border-radius: 14px;
    padding: 8px 10px;
  }
  .brand-lockup { gap: 8px; }
  .brand-lockup span {
    font-size: 21px;
  }
  .brand-lockup img {
    width: 34px;
    height: 34px;
  }
  .nav-actions { gap: 6px; }
  .language-pill,
  .mobile-toggle {
    width: 38px;
    height: 38px;
    border-radius: 11px;
  }
  .mobile-nav-panel {
    inset: 8px 8px auto 8px;
    max-height: calc(100svh - 16px);
    padding: 14px;
    border-radius: 14px;
  }
  .mobile-section--grid {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 370px) {
  .brand-lockup span { font-size: 19px; }
  .brand-lockup img { width: 32px; height: 32px; }
  .language-pill,
  .mobile-toggle { width: 36px; height: 36px; }
}
</style>



